import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import CodeBlock from './CodeBlock';
import ApiExplorer from './ApiExplorer';
import { useDocsConfig } from '../context/DocsConfigContext';

const EndpointCard = ({ method, path, title, description, tags = [], parameters = {}, requestBody = null, children, autoExamples = true }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('docs'); // 'docs' or 'try'
  const { baseUrl, apiKey } = useDocsConfig();

  const methodClasses = {
    'GET': 'method-get',
    'POST': 'method-post',
    'PUT': 'method-put',
    'PATCH': 'method-patch',
    'DELETE': 'method-delete',
  };

  const generated = useMemo(() => {
    if (!autoExamples) return null;
    const fullUrl = `${baseUrl}${path}`;
    const isAuthRequired = path !== '/users/';
    const body = requestBody && ['POST', 'PUT', 'PATCH'].includes(method) ? JSON.stringify(requestBody, null, 2) : null;

    const curl = [
      `curl -X ${method} "${fullUrl}"`,
      isAuthRequired ? `  -H "X-API-Key: ${apiKey || 'email@example.com:your_api_key_here'}"` : null,
      body ? `  -H "Content-Type: application/json"` : null,
      body ? `  -d '${body}'` : null,
    ].filter(Boolean).join(' \\\n');

    const js = [
      `fetch("${fullUrl}", {`,
      `  method: "${method}",`,
      `  headers: {`,
      isAuthRequired ? `    "X-API-Key": "${apiKey || 'email@example.com:your_api_key_here'}",` : null,
      body ? `    "Content-Type": "application/json",` : null,
      `  },`,
      body ? `  body: ${JSON.stringify(body)},` : null,
      `}).then(r => r.json()).then(console.log);`
    ].filter(Boolean).join('\n');

    const py = [
      `import requests`,
      ``,
      `url = "${fullUrl}"`,
      `headers = {`,
      isAuthRequired ? `    "X-API-Key": "${apiKey || 'email@example.com:your_api_key_here'}",` : null,
      body ? `    "Content-Type": "application/json",` : null,
      `}`,
      body ? `data = ${JSON.stringify(requestBody, null, 2)}` : `data = None`,
      `resp = requests.${method.toLowerCase()}(url, headers=headers${body ? ', json=data' : ''})`,
      `print(resp.status_code)`,
      `print(resp.json() if 'application/json' in resp.headers.get('Content-Type', '') else resp.text)`
    ].filter(Boolean).join('\n');

    return { curl, js, py };
  }, [autoExamples, baseUrl, path, method, requestBody, apiKey]);

  return (
    <div className={`endpoint-card card ${collapsed ? 'collapsed' : ''}`}>
      <div
        className="endpoint-header"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="endpoint-header-left">
          <span className={`endpoint-method ${methodClasses[method] || 'method-get'}`}>
            {method}
          </span>
          <span className="endpoint-path">{path}</span>
        </div>
        <ChevronDown className={`endpoint-chevron w-5 h-5 ${collapsed ? 'rotate-180' : ''}`} />
      </div>
      {!collapsed && (
        <div className="endpoint-content">
          <h3 className="font-semibold mb-2 text-gray-200">{title}</h3>
          {description && (
            <p className="endpoint-description">{description}</p>
          )}

          {/* Tabs */}
          <div className="mt-6 mb-4 border-b border-gray-700">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('docs')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'docs'
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Documentation
              </button>
              <button
                onClick={() => setActiveTab('try')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'try'
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Try it
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'docs' ? (
            <>
              {autoExamples && (
                <div className="space-y-3 mb-4">
                  <h4 className="text-sm font-semibold text-gray-300">Quick Examples</h4>
                  <CodeBlock tabs={{ curl: generated.curl, javascript: generated.js, python: generated.py }} />
                </div>
              )}
              {children}
              {tags.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <ApiExplorer
              method={method}
              path={path}
              description={description}
              parameters={parameters}
              requestBody={requestBody}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EndpointCard;
